<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading'],
  data() {
    return {
      document: null,        // { id, name, type }
      employees: [],
      selectedEmployeeId: '',
      errors: {},
      lastAssignment: null,  // { id, employee } after successful assign
      previewHtml: '',
      previewLoading: false,
      showPreview: false,
    };
  },
  mounted() {
    this.fetchEmployees();
  },
  methods: {
    async fetchEmployees() {
      try {
        const res = await axios.get('/hrms/employees/dropdown');
        this.employees = res.data.data || [];
      } catch (error) {
        console.log(error.message);
      }
    },

    employeeLabel(e) {
      const name = e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.name;
      return e.code ? `${name} (${e.code})` : name;
    },

    open(doc) {
      this.document = doc;
      this.selectedEmployeeId = '';
      this.errors = {};
      this.lastAssignment = null;
      this.previewHtml = '';
      this.showPreview = false;
    },

    async assign() {
      this.errors = {};
      if (!this.selectedEmployeeId) {
        this.errors.employee_id = ['Please select an employee.'];
        return;
      }
      this.setLoading(true);
      try {
        const res = await axios.post('/hrms/hrms-documents/assign', {
          employee_id: this.selectedEmployeeId,
          hrms_document_id: this.document.id,
        });
        this.$swalToast(res.data.message);
        this.lastAssignment = res.data.data;
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async loadPreview() {
      this.previewLoading = true;
      this.showPreview = true;
      try {
        const res = await axios.get(`/hrms/hrms-documents/${this.document.id}/preview`, {
          params: { employee_id: this.selectedEmployeeId },
        });
        this.previewHtml = res.data.data?.html || '';
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.previewLoading = false;
      }
    },

    async revoke() {
      if (!this.lastAssignment?.id) return;
      this.setLoading(true);
      try {
        const res = await axios.delete(`/hrms/hrms-documents/revoke/${this.lastAssignment.id}`);
        this.$swalToast(res.data.message);
        this.lastAssignment = null;
        this.showPreview = false;
        this.selectedEmployeeId = '';
      } catch (error) {
        this.$swalToast(error.message, 'error');
      } finally {
        this.setLoading(false);
      }
    },
  },
};
</script>

<template>
  <div class="modal fade" id="hrm_document_assign_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            Assign Document
            <span v-if="document" style="font-size:13px; color:#94a3b8; font-weight:500;">— {{ document.name }}</span>
          </h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body">

          <!-- Assign form -->
          <div class="mb-3">
            <label class="form-label">Employee <span class="text-danger">*</span></label>
            <div class="d-flex gap-2">
              <select class="form-select" v-model="selectedEmployeeId" :disabled="!!lastAssignment">
                <option value="">Select employee</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">{{ employeeLabel(e) }}</option>
              </select>
              <button
                v-if="!lastAssignment"
                type="button"
                class="btn btn-primary text-nowrap"
                @click="assign"
              >
                <i class="ti ti-send me-1"></i>Assign
              </button>
            </div>
            <div v-if="errors.employee_id" class="text-danger small mt-1">{{ errors.employee_id[0] }}</div>
          </div>

          <!-- Success state -->
          <div v-if="lastAssignment" class="doc-assign-success mb-3">
            <i class="ti ti-circle-check me-1"></i>
            Document assigned successfully. You can preview or revoke it below.
          </div>

          <div v-if="lastAssignment" class="d-flex gap-2 mb-3">
            <button type="button" class="btn btn-sm btn-light flex-fill" @click="loadPreview">
              <i class="ti ti-eye me-1"></i>Preview
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger flex-fill" @click="revoke">
              <i class="ti ti-x me-1"></i>Revoke
            </button>
          </div>

          <!-- Preview -->
          <div v-if="showPreview" class="doc-preview-box">
            <div v-if="previewLoading" class="text-center py-4 text-muted">
              <div class="spinner-border spinner-border-sm text-primary mb-2"></div>
              <div>Loading preview...</div>
            </div>
            <div v-else v-html="previewHtml"></div>
          </div>

        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.doc-assign-success {
  font-size: 12px; color: #16a34a;
  background: #f0fdf4; border: 1px solid #bbf7d0;
  border-radius: 8px; padding: 9px 12px;
}
.doc-preview-box {
  border: 1px solid #eef2f7; border-radius: 10px;
  padding: 16px; max-height: 320px; overflow-y: auto;
  background: #fff; font-size: 13px; color: #334155; line-height: 1.7;
}
</style>